// Section 15: a new pair forms a household and needs beds together (Section 9: a household needs
// beds for all its members in one building). The pair takes their dependent children living with
// them. With no building able to hold them all, they stay in their households (fertility 0.5x,
// age contributor) and try again each season.
import { mintId } from '../../core/ids';
import { isChild } from '../../core/queries';
import { sortedIds } from '../../core/sorted';
import type { BuildingId, HouseholdId, Person, PhaseContext } from '../../core/types';
import { bedsOf } from '../shared/housing';

/**
 * Free beds per building for this phase: counted once from the committed state (households as
 * the decide phase housed them) and kept up to date as couples move (Section 2 performance).
 */
export interface BedBook {
  free: Map<BuildingId, number>;
}

export function bedBook(ctx: PhaseContext): BedBook {
  const state = ctx.state;
  const free = new Map<BuildingId, number>();
  for (const id of sortedIds(state.buildings)) {
    const beds = bedsOf(ctx.registry, state, id);
    if (beds > 0) free.set(id, beds);
  }
  for (const hid of sortedIds(state.households)) {
    const h = state.households[hid];
    if (h === undefined || h.buildingId === null || !free.has(h.buildingId)) continue;
    free.set(h.buildingId, (free.get(h.buildingId) ?? 0) - h.memberIds.length);
  }
  return { free };
}

/** The tightest building with room for `n`, counting the movers' own beds as freed there. */
function fit(
  book: BedBook,
  n: number,
  leaving: ReadonlyMap<BuildingId, number>,
): BuildingId | null {
  let chosen: BuildingId | null = null;
  let room = Number.POSITIVE_INFINITY;
  for (const [id, free] of book.free) {
    const r = free + (leaving.get(id) ?? 0);
    if (r >= n && r < room) {
      chosen = id;
      room = r;
    }
  }
  return chosen;
}

/** The couple plus their children under age who live in one of the couple's households. */
function movers(ctx: PhaseContext, a: Person, b: Person): Person[] {
  const homes = new Set<HouseholdId>([a.householdId, b.householdId]);
  const out: Person[] = [a, b];
  for (const id of [...a.childIds, ...b.childIds].sort()) {
    const child = ctx.draft.people[id];
    if (child === undefined || out.includes(child)) continue;
    if (isChild(ctx.cfg, child) && homes.has(child.householdId)) out.push(child);
  }
  return out;
}

/**
 * Move the couple (and their children) into a new household in the tightest building with room.
 * Returns the new household id, or null when no building has room for them all.
 */
export function moveIn(ctx: PhaseContext, book: BedBook, a: Person, b: Person): HouseholdId | null {
  const group = movers(ctx, a, b);
  const ids = new Set(group.map((p) => p.id));
  const current = ctx.draft.households[a.householdId];
  if (
    a.householdId === b.householdId &&
    current !== undefined &&
    current.memberIds.every((id) => ids.has(id))
  )
    return current.id;
  const leaving = new Map<BuildingId, number>();
  for (const p of group) {
    const home = ctx.draft.households[p.householdId]?.buildingId ?? null;
    if (home !== null) leaving.set(home, (leaving.get(home) ?? 0) + 1);
  }
  const chosen = fit(book, group.length, leaving);
  if (chosen === null) return null;
  for (const [id, k] of leaving) book.free.set(id, (book.free.get(id) ?? 0) + k);
  book.free.set(chosen, (book.free.get(chosen) ?? 0) - group.length);
  const hid = mintId(ctx.draft, 'household');
  for (const p of group) {
    const old = ctx.draft.households[p.householdId];
    if (old !== undefined) {
      old.memberIds = old.memberIds.filter((id) => id !== p.id);
      if (old.memberIds.length === 0) delete ctx.draft.households[old.id];
    }
    p.householdId = hid;
  }
  ctx.draft.households[hid] = {
    id: hid,
    memberIds: group.map((p) => p.id).sort(),
    buildingId: chosen,
    foundedTurn: ctx.season.turn,
    isNewcomer: a.origin === 'newcomer' && b.origin === 'newcomer',
  };
  return hid;
}
